import { ArrowBackIcon } from "@chakra-ui/icons";
import {
  HStack,
  IconButton,
  Heading,
  Stack,
  UnorderedList,
  ListItem,
  OrderedList,
  Text,
  Divider,
  useColorModeValue,
  Icon,
} from "@chakra-ui/react";
import { Link, useNavigate } from "react-router-dom";
import { GuideInterface } from "../../constants/Guide";
import { RiInformation2Fill, RiLightbulbFill } from "@remixicon/react";
import { RiArrowRightLine } from "react-icons/ri";

interface GuideBookProps {
  guide: GuideInterface;
  role: string;
}

const GuideBook = ({ guide, role }: GuideBookProps) => {
  const navigate = useNavigate();

  const titleBgColor = useColorModeValue("blackAlpha.200", "whiteAlpha.200");
  const bgGradients = [
    "linear(to-tr, green.300, green.600)",
    "linear(to-tr, blue.300, blue.600)",
    "linear(to-tr, red.300, red.600)",
    "linear(to-tr, yellow.300, yellow.600)",
  ];

  return (
    <GuideWrapper>
      <HStack w="100%" maxW="600px" align="center">
        <IconButton
          aria-label="Back"
          size="md"
          variant="ghost"
          icon={<ArrowBackIcon boxSize={5} />}
          onClick={() => navigate(-1)}
        />
        <Heading as="h2" fontSize={14}>
          Panduan
        </Heading>
      </HStack>
      <Stack w="100%" maxW="600px" textAlign="justify">
        <Heading as="h1" fontSize={24} mb={4} textAlign="center">
          <Text as="b" color="red.500">
            BisnisLink
          </Text>
          : {guide.title}
        </Heading>
        <Divider />
        <Text>{guide.description}</Text>
      </Stack>
      <Stack w="100%" maxW="600px">
        <Stack
          p={4}
          rounded="xl"
          bgColor="green.800"
          color="white"
          boxShadow="md"
        >
          <Text as="b">{`Keuntungan menjadi ${role} BisnisLink:`}</Text>
        </Stack>
        <UnorderedList spacing={2} px={6} textAlign="justify">
          {guide.benefits.map((benefit, index) => (
            <ListItem key={index}>{benefit}</ListItem>
          ))}
        </UnorderedList>

        <Stack spacing={2} py={4}>
          <Stack p={4} rounded="xl" bgColor={titleBgColor} boxShadow="md">
            <Heading as="h3" fontSize={14}>
              {guide.steps.title}
            </Heading>
          </Stack>
          <OrderedList spacing={4} px={6} textAlign="justify">
            {guide.steps.steps.map((step, index) => (
              <ListItem key={index}>
                <Text as="b">{step.description}</Text>
                {step.subSteps && (
                  <UnorderedList spacing={2} mt={2}>
                    {step.subSteps.map((subStep, index) => (
                      <ListItem key={index}>
                        {subStep.description}
                        {subStep.subSubSteps && (
                          <OrderedList spacing={2} px={4} mt={2}>
                            {subStep.subSubSteps.map((subSubStep, index) => (
                              <ListItem key={index}>{subSubStep}</ListItem>
                            ))}
                          </OrderedList>
                        )}
                      </ListItem>
                    ))}
                  </UnorderedList>
                )}
              </ListItem>
            ))}
          </OrderedList>
        </Stack>

        {guide.advertisement && (
          <Stack spacing={4} w="full">
            {guide.advertisement.map((ad, index) => (
              <HStack
                key={index}
                as={Link}
                to="/"
                spacing={2}
                justify="space-between"
                w="full"
                p={8}
                rounded="xl"
                bgGradient={bgGradients[index % 4]}
                color="white"
                boxShadow="md"
              >
                <Text as="b" fontSize="md">
                  {ad}
                </Text>
                <Icon as={RiArrowRightLine} boxSize={6} />
              </HStack>
            ))}
          </Stack>
        )}

        <Stack
          my={8}
          spacing={2}
          rounded="xl"
          bgColor={titleBgColor}
          boxShadow="md"
          overflow="hidden"
        >
          <HStack p={4} bgColor="yellow.500" color="white" w="full">
            <Icon as={RiLightbulbFill} boxSize={6} />
            <Heading as="h3" fontSize={14}>
              Tips Sukses
            </Heading>
          </HStack>
          <UnorderedList spacing={2} px={4} pb={4} pt={2} textAlign="justify">
            {guide.successTips.map((tip, index) => (
              <ListItem key={index}>{tip}</ListItem>
            ))}
          </UnorderedList>
        </Stack>

        {guide.promoTips && (
          <Stack
            mb={8}
            spacing={2}
            rounded="xl"
            bgColor={titleBgColor}
            boxShadow="md"
            overflow="hidden"
          >
            <HStack p={4} bgColor="blue.500" color="white" w="full">
              <Icon as={RiLightbulbFill} boxSize={6} />
              <Heading as="h3" fontSize={14}>
                Tips Promo
              </Heading>
            </HStack>
            <UnorderedList spacing={2} px={4} pb={4} pt={2} textAlign="justify">
              {guide.promoTips.map((tip, index) => (
                <ListItem key={index}>{tip}</ListItem>
              ))}
            </UnorderedList>
          </Stack>
        )}

        <Stack spacing={2}>
          <HStack spacing={2}>
            <Icon as={RiInformation2Fill} boxSize={4} />
            <Heading as="h3" fontSize={14}>
              Informasi Tambahan
            </Heading>
          </HStack>
          <UnorderedList spacing={2} px={4} textAlign="justify">
            {guide.additionalInfo.map((info, index) => (
              <ListItem key={index}>{info}</ListItem>
            ))}
          </UnorderedList>
        </Stack>
      </Stack>
    </GuideWrapper>
  );
};

export default GuideBook;

const GuideWrapper = ({ children }: { children: React.ReactNode }) => {
  return (
    <Stack
      gap={4}
      px={8}
      pt={4}
      pb={12}
      align="center"
      w="100%"
      h="100%"
      fontSize={14}
    >
      {children}
    </Stack>
  );
};
